import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useMatches } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ColumnCenter } from "../components/ColumnCenter";
import { ColumnLeft } from "../components/ColumnLeft";
import { ColumnRight, getMenuGraphic } from "../components/ColumnRight";
import type { MenuItem } from "../components/ColumnRight";
import { Footer } from "../components/Footer";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { MobileMenu } from "../components/MobileMenu";
import { useSeo } from "../hooks/useSeo";
import type { LocaleKey } from "../utils/svgPlaceholders";

interface RouteHandle {
  locale: LocaleKey;
  localePrefix: "" | "en";
}

const defaultLocale: LocaleKey = "zh-Hant";

const pageTitleSlugByKey: Record<string, string> = {
  "competition-details": "competition-details",
  "join-now": "join-now",
  "event-details": "quiz-final",
  "try-it-now": "try-it-now",
  "related-websites": "related-websites",
};

const getPageTitleGraphic = (locale: LocaleKey, key: string) => {
  const slug = pageTitleSlugByKey[key];
  if (!slug) {
    return undefined;
  }
  const suffix = locale === "en" ? "-en" : "";
  return `/basiclaw2025-26/assets/page-title/${slug}-title${suffix}.png`;
};

export const AppShell = () => {
  const matches = useMatches();
  const location = useLocation();
  const { i18n, t } = useTranslation();
  const [tryItNowMenuSignal, setTryItNowMenuSignal] = useState(0);

  const activeHandle = [...matches]
    .reverse()
    .find((match) => (match.handle as RouteHandle | undefined)?.locale);

  const { locale, localePrefix } = (activeHandle?.handle as RouteHandle) ?? {
    locale: defaultLocale,
    localePrefix: "" as const,
  };

  useSeo({ locale });

  useEffect(() => {
    if (i18n.language !== locale) {
      void i18n.changeLanguage(locale);
    }
    document.documentElement.lang = locale === "en" ? "en" : "zh-Hant";
    document.documentElement.setAttribute("data-locale", locale);
  }, [i18n, locale]);

  const isHome =
    location.pathname === "/" ||
    location.pathname === "" ||
    location.pathname === "/en/" ||
    location.pathname === "/en";

  useEffect(() => {
    document.body.style.setProperty(
      "--app-shell-background",
      "url('/basiclaw2025-26/assets/bg-light.png')",
    );

    return () => {
      document.body.style.removeProperty("--app-shell-background");
    };
  }, [location.pathname]);

  const menuItems = useMemo<MenuItem[]>(
    () => [
      {
        key: "home",
        path: "",
        label: t("navigation.home"),
        accent: { start: "#ec4899", end: "#f97316" },
        graphic: getMenuGraphic(locale, "home"),
      },
      {
        key: "competition-details",
        path: "competition-details",
        label: t("navigation.competitionDetails"),
        accent: { start: "#6366f1", end: "#8b5cf6" },
        graphic: getMenuGraphic(locale, "competition-details"),
      },
      {
        key: "join-now",
        path: "join-now",
        label: t("navigation.joinNow"),
        accent: { start: "#22c55e", end: "#16a34a" },
        graphic: getMenuGraphic(locale, "join-now"),
      },
      {
        key: "event-details",
        path: "event-details",
        label: t("navigation.eventDetails"),
        accent: { start: "#0ea5e9", end: "#38bdf8" },
        graphic: getMenuGraphic(locale, "quiz-final"),
      },
      {
        key: "try-it-now",
        path: "try-it-now",
        label: t("navigation.tryItNow"),
        accent: { start: "#f59e0b", end: "#f97316" },
        graphic: getMenuGraphic(locale, "try-it-now"),
      },
      {
        key: "related-websites",
        path: "related-websites",
        label: t("navigation.relatedWebsites"),
        accent: { start: "#6366f1", end: "#3b82f6" },
        graphic: getMenuGraphic(locale, "related-websites"),
      },
    ],
    [locale, t],
  );

  const trimmedPath = location.pathname.replace(/^\/|\/$/g, "");
  const relativePath = (() => {
    if (locale === "en") {
      if (trimmedPath === "en" || trimmedPath === "en/") {
        return "";
      }
      return trimmedPath.startsWith("en/")
        ? trimmedPath.slice("en/".length)
        : trimmedPath;
    }
    return trimmedPath;
  })();

  const currentMenuItem = menuItems.find((item) =>
    item.path
      ? item.path === relativePath
      : relativePath === "" && item.path === "",
  );

  const pageGraphic =
    !isHome && currentMenuItem
      ? getPageTitleGraphic(locale, currentMenuItem.key)
      : undefined;
  const pageGraphicLabel = !isHome ? currentMenuItem?.label : undefined;

  const handleMenuNavigate = useCallback(
    (key: string) => {
      if (key === "try-it-now") {
        setTryItNowMenuSignal((value) => value + 1);
      }
    },
    [],
  );

  const contentClassName = isHome
    ? "app-shell__content app-shell__content--home"
    : "app-shell__content app-shell__content--inner";

  return (
    <div className="app-shell">
      <MobileMenu
        locale={locale}
        items={menuItems}
        onMenuNavigate={handleMenuNavigate}
      />
      <div className={contentClassName}>
{/*         {isHome ? ( */}
          <aside className="app-shell__column app-shell__column--left">
            <ColumnLeft locale={locale} showDescription={isHome} />
          </aside>
{/*         ) : null} */}
        <main className="app-shell__column app-shell__column--center">
          <ColumnCenter
            locale={locale}
            pageGraphic={pageGraphic}
            pageGraphicLabel={pageGraphicLabel}
          >
            <Outlet
              context={{ locale, localePrefix, tryItNowMenuSignal }}
            />
          </ColumnCenter>
        </main>
        <nav className="app-shell__column app-shell__column--right">
          <ColumnRight
            locale={locale}
            items={menuItems}
            onMenuNavigate={handleMenuNavigate}
          >
            <LanguageSwitcher locale={locale} />
          </ColumnRight>
        </nav>
      </div>
      <div className="app-shell__lower-banner">
        <img
          src="/basiclaw2025-26/assets/lower-banner.png"
          alt={t("layout.center.bottomBannerAlt")}
        />
      </div>
      <Footer />
    </div>
  );
};
